import { Season } from '@shared/constants';
import { categoryStatsConverter, categoryStatsToDoc } from 'api/converters/CategoryStatsConverter';
import { ActionCls, CrimsonCls, Emojis, END, ValueCls, ValueLgCls } from 'app/constants';
import { collection, doc, getDocs, setDoc, writeBatch } from 'firebase/firestore';
import { breakSeasonWeekID } from 'helpers/IdHelpers';
import {
  CategoryNameT,
  CategoryStats,
  IRankingCategories,
  IWeekRank,
  RankingsPayload,
  SeasonID,
  SeasonWeekID,
  WeekID,
} from 'types';
import { db } from '../lib/firebase';

/**
 * Fetches statistics for a specific week and year from Firebase DB.
 *
 * @param {WeekID} weekID - e.g. 'Week1'
 * @param {SeasonID} year - season year
 * @param {SeasonWeekID} seasonWeekID - format: 'seasonYear-weekID', e.g. '2024-Week1'
 *
 * @return {Promise<RankingsPayload>} - promise that resolves to RankingsPayload containing weekly and season rankings
 */
export const fetchStatisticsForWeek = async (
  weekID: WeekID,
  year: SeasonID,
  seasonWeekID: SeasonWeekID,
): Promise<RankingsPayload> => {
  const collectionName = `stats/${year}/stats-${seasonWeekID}`;
  const collectionData = collection(db, collectionName).withConverter(categoryStatsConverter);
  const snapshot = await getDocs(collectionData);
  const stats: CategoryStats[] = [];
  snapshot.forEach((doc) => {
    const stat = doc.data();
    stats.push(stat);
  });

  if (stats.length === 0) {
    return {
      calculated: false,
      rankings: {},
      seasonRankings: {},
      weekID: weekID,
      yearID: year,
      seasonWeekID: seasonWeekID,
    };
  }

  const rankings: IRankingCategories = {};
  const seasonRankings: IRankingCategories = {};
  stats.forEach((stat) => {
    const { id, seasonRanking, weeklyRanking, rankingCategory, seasonAdjustedRanking, week, weeklyAdjustedRanking } =
      stat;
    rankings[id] = {
      rankCategory: rankingCategory as CategoryNameT,
      ranking: weeklyRanking,
      adjustedRanking: weeklyAdjustedRanking,
      weekID: week,
    } as IWeekRank;
    seasonRankings[id] = {
      rankCategory: rankingCategory as CategoryNameT,
      ranking: seasonRanking,
      adjustedRanking: seasonAdjustedRanking,
      weekID: week,
    } as IWeekRank;
  });

  const rankingPayload: RankingsPayload = {
    rankings: rankings,
    seasonRankings: seasonRankings,
    weekID: weekID,
    calculated: true,
    seasonWeekID: seasonWeekID,
    yearID: year,
  };

  return rankingPayload;
};

/**
 * Saves a single category statistic into Firebase DB.
 *
 * @param {CategoryStats} categoryStat - category statistics to be saved
 * @param {SeasonID} seasonYear - season year
 * @param {number} weekNumber - week number
 * @return {Promise<void>} - promise that resolves when write to Firebase DB is completed
 */
export const saveCategoryStatistic = async (
  categoryStat: CategoryStats,
  seasonYear: SeasonID,
  weekNumber: number,
): Promise<void> => {
  const collectionName = `stats${seasonYear}week${weekNumber}`;
  const documentId = categoryStat.id;
  const collectionData = collection(db, collectionName).withConverter(categoryStatsConverter);
  const docRef = doc(collectionData, documentId);

  return await setDoc(docRef, categoryStatsToDoc(categoryStat));
};

/**
 * Saves all ranked categories for a given week and year into Firebase DB.
 * This function writes to `stats/yearID/yearID-weekID/doc.id`
 *
 * @param categoryStats list of categories for a given week to be saved
 * @param year season year
 * @param seasonWeekID `season year - week id` to identify these categories by
 *
 * @return {Promise<void>} - promise that resolves when write to Firebase DB is completed
 */
export const saveCategoryStatisticsByWeekAndYear = async (
  categoryStats: CategoryStats[],
  year: SeasonID,
  seasonWeekID: SeasonWeekID,
): Promise<void> => {
  const batch = writeBatch(db);
  const collectionName = `stats/${year}/stats-${seasonWeekID}`;
  const collectionData = collection(db, collectionName).withConverter(categoryStatsConverter);
  categoryStats.forEach((stat) => {
    const docRef = doc(collectionData, stat.id);
    batch.set(docRef, categoryStatsToDoc(stat));
  });
  await batch.commit();
  console.log(`${Emojis.eyes} %c BATCH WRITING ${END} %c stats/${year}/${seasonWeekID} ${END}`, ActionCls, ValueCls);

  return;
};

/**
 * Converts a RankingsPayload into a list of CategoryStats for easier saving into Firebase DB.
 *
 * @param {RankingsPayload} stats - rankings payload containing weekly and season rankings
 * @param {SeasonID} yearID (optional) if not provided, will not be set in the resulting CategoryStats
 * @param {SeasonWeekID} seasonWeekID (optional) if not provided, will not be set in the resulting CategoryStats
 *
 * @return {CategoryStats[]} - list of category stats for the week
 */
export const convertRankWeekIntoCategoryStats = (
  stats: RankingsPayload,
  yearID?: SeasonID,
  seasonWeekID?: SeasonWeekID,
): CategoryStats[] => {
  const { rankings, weekID, seasonRankings } = stats;
  const rankingKeys = Object.keys(rankings).sort();
  const seasonKeys = Object.keys(seasonRankings).sort();

  if (rankingKeys.toString() === seasonKeys.toString()) {
    const weekCategoryStats = rankingKeys.map((key) => {
      const categoryStat: IWeekRank = rankings[key]!;
      const seasonStat: IWeekRank = seasonRankings[key]!;
      return {
        id: key,
        rankingCategory: categoryStat.rankCategory,
        seasonAdjustedRanking: seasonStat.adjustedRanking,
        seasonRanking: seasonStat.ranking,
        weeklyRanking: categoryStat.ranking,
        weeklyAdjustedRanking: categoryStat.adjustedRanking,
        week: weekID,
        year: yearID,
        seasonWeekID: seasonWeekID,
      } as CategoryStats;
    });
    return weekCategoryStats;
  } else {
    throw Error('STATS KEYS DO NOT MATCH');
  }
};

/**
 * Saves category statistics for a specific season week.
 *
 * @deprecated Use `saveCategoryStatisticsByWeekAndYear` instead
 *
 * @param {CategoryStats[]} categoryStats list of categories to be saved
 * @param {SeasonID} year season year
 * @param {number} weekNumber week number
 *
 * @return {Promise<void>} - promise that resolves when write to Firebase DB is completed
 */
export const saveCategoryStatisticsForWeek = async (
  categoryStats: CategoryStats[],
  year: SeasonID = 'seasonYear',
  weekNumber: number,
): Promise<void> => {
  const batch = writeBatch(db);
  const collectionName = `stats${year}week${weekNumber}`;
  const collectionData = collection(db, collectionName).withConverter(categoryStatsConverter);
  categoryStats.forEach((stat) => {
    const docRef = doc(collectionData, stat.id);
    batch.set(docRef, categoryStatsToDoc(stat));
  });
  console.log(`${Emojis.eyes} %c BATCH WRITING ${END} %c stats${year}week${weekNumber} ${END}`, ActionCls, ValueCls);
  return await batch.commit();
};

/**
 * Helper function to save all rankings in Firebase.
 *
 * @deprecated Use `saveCategoryStatisticsByWeekAndYear` instead
 *
 * @param {RankingsPayload[]} stats rankings to be saved in Firebase Database
 *
 * @return {Promise<void>} - promise that resolves when write to Firebase DB is completed
 */
export const saveCategoryStatisticsForRegularSeason = async (stats: RankingsPayload[]): Promise<void> => {
  const weekCat: CategoryStats[][] = [];
  const seasonYear = stats[0].seasonWeekID ? breakSeasonWeekID(stats[0].seasonWeekID).yearID : Season.Season2024;
  stats.forEach((stat: RankingsPayload) => {
    try {
      const weekCategoryStats = convertRankWeekIntoCategoryStats(stat);
      weekCat.push(weekCategoryStats);
    } catch (e: any) {
      console.log(`${Emojis.eyes} %c ${e} ${END} %c KEYS NOT EQUAL ${END}`, ValueCls, CrimsonCls);
    }
  });

  console.dir(weekCat);

  if (weekCat.length === 18) {
    console.log(`${Emojis.eyes} LENGTH %c ${weekCat.length} ${END}`, ValueLgCls);

    await saveCategoryStatisticsForWeek(weekCat[0], seasonYear, 1);
    await saveCategoryStatisticsForWeek(weekCat[1], seasonYear, 2);
    await saveCategoryStatisticsForWeek(weekCat[2], seasonYear, 3);
    await saveCategoryStatisticsForWeek(weekCat[3], seasonYear, 4);
    await saveCategoryStatisticsForWeek(weekCat[4], seasonYear, 5);
    await saveCategoryStatisticsForWeek(weekCat[5], seasonYear, 6);
    // TODO: Apparently we need to take a small pause here as the writer chokes
    await saveCategoryStatisticsForWeek(weekCat[6], seasonYear, 7);
    await saveCategoryStatisticsForWeek(weekCat[7], seasonYear, 8);
    await saveCategoryStatisticsForWeek(weekCat[8], seasonYear, 9);
    await saveCategoryStatisticsForWeek(weekCat[9], seasonYear, 10);
    await saveCategoryStatisticsForWeek(weekCat[10], seasonYear, 11);
    await saveCategoryStatisticsForWeek(weekCat[11], seasonYear, 12);
    // TODO: Apparently we need to take a small pause here as the writer chokes
    await saveCategoryStatisticsForWeek(weekCat[12], seasonYear, 13);
    await saveCategoryStatisticsForWeek(weekCat[13], seasonYear, 14);
    await saveCategoryStatisticsForWeek(weekCat[14], seasonYear, 15);
    await saveCategoryStatisticsForWeek(weekCat[15], seasonYear, 16);
    await saveCategoryStatisticsForWeek(weekCat[16], seasonYear, 17);
    await saveCategoryStatisticsForWeek(weekCat[17], seasonYear, 18);
    console.log(`${Emojis.eyes} %c BATCH WRITING ${END} %c stats2024week1-18 ${END}`, ActionCls, ValueCls);
  }
};
